import { Drawer as MuiDrawer, styled } from '@mui/material';
import { getNavigationItems } from 'components/Navigation/navigation-config';
import { getNavigableAssignments, isActivePath } from 'components/Navigation/navigation-utils';
import NavigationContent from 'components/Navigation/NavigationContent';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSidebarLayout } from 'contexts/sidebarLayout';
import { useViewport } from 'contexts/viewport';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, IconButton, Logo, Stack, Text } from 'shell/components';
import { ViewportDevice } from 'utils/constants';

const DESKTOP_NAV_STATE_KEY = 'timelygrader.vertical-navigation-expanded';
const SIDEBAR_WIDTH = { expanded: 205, collapsed: 90 };

const Navigation = () => {
    const { auth, currentUser } = useAuth();
    const { assignments: allAssignments, course } = useApp();
    const { device } = useViewport();
    const { guard } = useNavigationGuard();
    const { setSidebarWidth } = useSidebarLayout();
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [desktopExpanded, setDesktopExpanded] = useState(() => {
        try {
            const saved = localStorage.getItem(DESKTOP_NAV_STATE_KEY);
            return saved === null ? true : saved === 'true';
        } catch {
            return true;
        }
    });
    const [tabletExpanded, setTabletExpanded] = useState(false);
    const previousDevice = useRef(device);
    const items = useMemo(() => getNavigationItems(auth?.role, t), [auth?.role, t]);
    const assignments = useMemo(
        () => getNavigableAssignments(allAssignments, auth?.role),
        [allAssignments, auth?.role]
    );
    const expanded =
        device === ViewportDevice.DESKTOP ? desktopExpanded : device === ViewportDevice.TABLET ? tabletExpanded : false;
    const mobileTitle = useMemo(
        () =>
            items.find((item) => isActivePath(location.pathname, item.link))?.text || t('nav.navigation', 'Navigation'),
        [items, location.pathname, t]
    );

    useEffect(() => {
        if (device === ViewportDevice.TABLET && previousDevice.current !== ViewportDevice.TABLET)
            setTabletExpanded(false);
        previousDevice.current = device;
    }, [device]);

    useLayoutEffect(() => {
        const width =
            device === ViewportDevice.MOBILE ? 0 : expanded ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed;
        setSidebarWidth(width);

        return () => setSidebarWidth(0);
    }, [device, expanded, setSidebarWidth]);

    useEffect(() => {
        if (device !== ViewportDevice.DESKTOP) return;
        try {
            localStorage.setItem(DESKTOP_NAV_STATE_KEY, String(desktopExpanded));
        } catch {
            // Storage can be unavailable in embedded/private contexts.
        }
    }, [desktopExpanded, device]);

    const navigate = (path) => {
        if (!path) return;
        guard(() => {
            history.push(path);
            setMobileOpen(false);
        });
    };

    const contentProps = {
        course,
        currentUser,
        assignments,
        expanded,
        items,
        onNavigate: navigate,
        pathname: location.pathname,
    };

    if (device === ViewportDevice.MOBILE) {
        return (
            <MobileNavigationRoot>
                <MobileBar>
                    <Logo device={ViewportDevice.MOBILE} />
                    <Text variant={'p1'} weight={'semibold'}>
                        {mobileTitle}
                    </Text>
                    <IconButton
                        aria-label={
                            mobileOpen
                                ? t('nav.close-navigation', 'Close navigation')
                                : t('nav.open-navigation', 'Open navigation')
                        }
                        kind={'hamburger'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setMobileOpen((value) => !value)}
                    />
                </MobileBar>
                <MobileDrawer anchor={'right'} open={mobileOpen} onClose={() => setMobileOpen(false)}>
                    <NavigationContent {...contentProps} expanded mobile onToggle={() => setMobileOpen(false)} />
                </MobileDrawer>
            </MobileNavigationRoot>
        );
    }

    return (
        <SidebarRoot
            aria-label={t('nav.primary-navigation', 'Primary navigation')}
            expanded={expanded}
            role={'navigation'}
        >
            <NavigationContent
                {...contentProps}
                onToggle={() => {
                    if (device === ViewportDevice.DESKTOP) setDesktopExpanded((value) => !value);
                    if (device === ViewportDevice.TABLET) setTabletExpanded((value) => !value);
                }}
            />
        </SidebarRoot>
    );
};

const SidebarRoot = styled(Box)(({ expanded, theme }) => ({
    position: 'relative',
    width: expanded ? SIDEBAR_WIDTH.expanded : SIDEBAR_WIDTH.collapsed,
    height: '100%',
    flexShrink: 0,
    zIndex: theme.zIndex.drawer + 3,
    background: theme.palette.ensightful.shades[0],
    borderRight: `1px solid ${theme.palette.ensightful.neutral[200]}`,
    transition: 'width 250ms ease',
}));

const MobileNavigationRoot = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    height: 64,
    flexShrink: 0,
    zIndex: theme.zIndex.drawer + 3,
    background: theme.palette.ensightful.neutral[50],
}));

const MobileBar = styled(Stack)(({ theme }) => ({
    height: 64,
    paddingInline: theme.spacing(2.5),
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: 'row',
    background: theme.palette.ensightful.shades[0],
    borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
}));

const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 4,
    '& .MuiPaper-root': {
        width: '100vw',
        maxWidth: '100vw',
        height: '100vh',
        padding: theme.spacing(2.5),
        background: theme.palette.ensightful.shades[0],
    },
}));

export default Navigation;
